/**
 * Remembers which Clerk organization a salon short code points to (`12345 → org_abc`) across page loads, so a
 * reload opens the salon without first waiting on `GET /public/organizations/resolve/:shortCode`. The mapping is
 * public (it is in every salon URL and API request) and never changes for a live salon; callers revalidate it in
 * the background and drop it when the salon is gone. Every read and write tolerates blocked or corrupt storage.
 */

const STORAGE_KEY = 'hayven:org-resolve:v1';
/** An agency person may open many salons; keep the most recently used ones. */
const MAX_ENTRIES = 100;

export type OrgResolution = { orgId: string; shortCode: string };
type StoredEntries = Record<string, { orgId: string; savedAt: number }>;

const SHORT_CODE_PATTERN = /^\d{1,6}$/;
const CLERK_ORG_ID_PATTERN = /^org_[a-zA-Z0-9]+$/;

function readEntries(): StoredEntries {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const entries: StoredEntries = {};
    for (const [shortCode, value] of Object.entries(parsed as Record<string, unknown>)) {
      const entry = value as { orgId?: unknown; savedAt?: unknown } | null;
      if (
        SHORT_CODE_PATTERN.test(shortCode) &&
        typeof entry?.orgId === 'string' &&
        CLERK_ORG_ID_PATTERN.test(entry.orgId) &&
        typeof entry.savedAt === 'number'
      ) {
        entries[shortCode] = { orgId: entry.orgId, savedAt: entry.savedAt };
      }
    }
    return entries;
  } catch {
    return {};
  }
}

function writeEntries(entries: StoredEntries): void {
  try {
    const newestFirst = Object.entries(entries).sort(([, a], [, b]) => b.savedAt - a.savedAt);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(newestFirst.slice(0, MAX_ENTRIES))));
  } catch {
    // Storage full or blocked: the app still works, the next load just resolves over the network.
  }
}

/** The saved mapping for a short code or Clerk org id, or null. */
export function readStoredOrgResolution(identifier: string): OrgResolution | null {
  const entries = readEntries();
  const byShortCode = entries[identifier];
  if (byShortCode) return { orgId: byShortCode.orgId, shortCode: identifier };
  const match = Object.entries(entries).find(([, entry]) => entry.orgId === identifier);
  return match ? { orgId: match[1].orgId, shortCode: match[0] } : null;
}

export function storeOrgResolution({ orgId, shortCode }: OrgResolution): void {
  if (!SHORT_CODE_PATTERN.test(shortCode) || !CLERK_ORG_ID_PATTERN.test(orgId)) return;
  const entries = readEntries();
  // One short code per org: a stale code that pointed at this org goes too.
  for (const [code, entry] of Object.entries(entries)) {
    if (entry.orgId === orgId) delete entries[code];
  }
  entries[shortCode] = { orgId, savedAt: Date.now() };
  writeEntries(entries);
}

/** Drops whatever is saved under this short code or org id (salon deleted, or the code now points elsewhere). */
export function forgetStoredOrgResolution(identifier: string): void {
  const entries = readEntries();
  let changed = false;
  for (const [code, entry] of Object.entries(entries)) {
    if (code === identifier || entry.orgId === identifier) {
      delete entries[code];
      changed = true;
    }
  }
  if (changed) writeEntries(entries);
}

/** Sign-out: the next person on this computer starts without the previous person's salon list. */
export function clearOrgResolveStorage(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage has nothing to clear.
  }
}
